import { SortDirection } from '../../../overviews/generic-overview/components/display-settings-bar/entities/sort-direction.entity';
import { SortField } from '../../../overviews/generic-overview/components/display-settings-bar/entities/sort-field.entity';
import { ApiQuery, Restriction } from '../../api/entities/api-query.entity';
import { ApiQueryBuilder } from '../../api/helpers/api-query-builder.helper';
import { Operand } from '../../query/entities/operand.entity';
import { StructuredQuery } from '../../query/entities/structured-query.entity';
import { GenerateStructuredQueryFromAdvancedSearchTerm } from '../../query/helpers/structured-query.helper';
import { FragmentId } from '../../records/entities/metadata/metadata.entity';
import { PartialRecord } from '../../records/entities/partial-record.class';
import {
    AdoptTask,
    BatchCsvTask,
    BatchTask,
    DeleteTask,
    DiffTask,
    MergeTask,
    MigrateDateTask,
    MultipleMergeTask,
    PublishTask,
    RestartJobsTask,
    RetranscodeTask,
    TouchTask
} from './batch-tasks.entity';

/**
 * Batch options object POSTed to the /batches endpoint in order to create a Batch job.
 * @param TaskType {@link BatchTypes}
 * @param Task {@link OptionsMap}
 * @param SearchRequest {@link SearchRequest}
 * @param Label The label to identify the batch (optional).
 * @param Slices The number of parallel jobs, ranging from 1 to 128. Must be 1 for BatchDeleteTask. (optional) @default 1
 * @param Propagate Propagate changes to records with inheritance relationships. (optional) @default false
 * @param Indices The list of indices the batch will use to search for records. (optional) @default All indices the user has access to
 *
 * @see https://mh-tst.mediahaven.com/mediahaven-rest-api/v2/api-docs/index.html#mediahaven-rest-api-manual-batches-batch-create-object
 */
export class BatchOptions<T extends BatchTaskType> {
    private TaskType: T;
    private Task: OptionsMap[T];
    private SearchRequest: SearchRequest;
    private readonly Label?: string;
    private readonly Slices?: number;
    private readonly Propagate?: boolean;
    private readonly Indices?: string[];
    private Tag: string;

    constructor(taskType: T, taskOptions: Partial<OptionsMap[T]>, searchRequest: SearchRequest, tag: string) {
        this.createBatchOptions(taskType, taskOptions, searchRequest, tag);
    }

    public createBatchOptions(
        taskType: T,
        taskOptions: Partial<OptionsMap[T]>,
        searchRequest: SearchRequest,
        tag: string
    ): void {
        this.TaskType = taskType;
        this.Task = this.createTask(taskType, taskOptions);
        this.SearchRequest = searchRequest;
        this.Tag = tag;
    }

    public getTask(): BatchTask {
        return this.Task;
    }

    public getTaskType(): BatchTaskType {
        return this.TaskType;
    }

    public getSearchRequest(): SearchRequest {
        return this.SearchRequest;
    }

    private createTask(taskType: T, taskOptions: Partial<OptionsMap[T]>): OptionsMap[T] {
        const batchOptionsMapping: {
            [key in BatchTaskType]: BatchOptionsMapped<key>;
        } = {
            [ShBatchTaskTypes.MERGE]: this.mergeBatchOptions,
            [ShBatchTaskTypes.MERGE_MULTIPLE]: this.mergeMultipleBatchOptions,
            [ShBatchTaskTypes.ADOPT]: this.adoptBatchOptions,
            [ShBatchTaskTypes.DIFF]: this.undefinedBatchOptions,
            [ShBatchTaskTypes.DELETE]: this.deleteBatchOptions,
            [ShBatchTaskTypes.PUBLISH]: this.publishBatchOptions,
            [ShBatchTaskTypes.RESTART_FAILED_JOBS]: this.undefinedBatchOptions,
            [ShBatchTaskTypes.TOUCH]: this.undefinedBatchOptions,
            [ShBatchTaskTypes.RETRANSCODE]: this.undefinedBatchOptions,
            [ShBatchTaskTypes.MIGRATE_DATE_FIELDS]: this.undefinedBatchOptions,
            [ShBatchTaskTypes.CSV_TASK]: this.CsvBatchOptions
        };

        return batchOptionsMapping[taskType](taskOptions);
    }

    private mergeBatchOptions(taskOptions: Partial<OptionsMap[ShBatchTaskTypes.MERGE]>): MergeTask {
        if (!taskOptions.Sidecar) {
            throw new Error(
                `BatchService:: expected taskOptions not to be undefined. Missing Sidecar: ${taskOptions.Sidecar}`
            );
        }

        return {
            Sidecar: taskOptions.Sidecar,
            Reason: taskOptions.Reason ?? 'Bulk save after bulk describe: MERGE'
        };
    }

    private CsvBatchOptions(taskOptions: Partial<OptionsMap[ShBatchTaskTypes.CSV_TASK]>): BatchCsvTask {
        if (!taskOptions.ProfileId || !taskOptions.ExportFileName) {
            throw new Error(`BatchService:: expected taskOptions not to be undefined. Missing Sidecar: ${taskOptions}`);
        }

        return {
            ProfileId: taskOptions.ProfileId,
            ExportFileName: taskOptions.ExportFileName,
            HeaderType: taskOptions.HeaderType
        };
    }

    private mergeMultipleBatchOptions(
        taskOptions: Partial<OptionsMap[ShBatchTaskTypes.MERGE_MULTIPLE]>
    ): MultipleMergeTask {
        if (!taskOptions.Sidecars) {
            throw new Error(
                `BatchService:: expected taskOptions not to be undefined. Missing Sidecars: ${taskOptions.Sidecars}`
            );
        }

        return {
            Sidecars: taskOptions.Sidecars,
            Reason: taskOptions.Reason ?? 'Bulk save after bulk describe: MERGES'
        };
    }

    private adoptBatchOptions(taskOptions: Partial<OptionsMap[ShBatchTaskTypes.ADOPT]>): AdoptTask {
        if (!taskOptions.ParentRecordId) {
            throw new Error(
                `BatchService:: expected taskOptions not to be undefined. Missing ParentRecordId: ${taskOptions.ParentRecordId}`
            );
        }

        return {
            ParentRecordId: taskOptions.ParentRecordId,
            Reason: taskOptions.Reason ?? 'Batch change parents for records in search request: ADOPT',
            PreventForcedInheritance: true
        };
    }

    private publishBatchOptions(taskOptions: OptionsMap[ShBatchTaskTypes.PUBLISH]): PublishTask {
        return {
            StrictValidation: true
        };
    }

    private deleteBatchOptions(taskOptions: OptionsMap[ShBatchTaskTypes.DELETE]): DeleteTask {
        return {
            PreventCascade: false
        };
    }

    private undefinedBatchOptions<L extends BatchTaskType, K extends BatchTask>(
        taskOptions: Partial<OptionsMap[L]>
    ): K {
        throw new Error('BatchService:: This BatchTaskType has not yet been implemented');
    }
}

export interface SearchRequest {
    Query: string;
    SortField?: SortField;
    SortDirection?: SortDirection;
}

/**
 * The documentation does not indicate that the SearchQuery is required, but it is.
 * The query is what is used to retrieve the records. The Sidecar is what is applied to those records.
 * Sending two whole records as the Sidecar will result in Engine breaking, as I have learnt.
 *
 * Only the query is strictly required. Both the SortField and SortDirection properties receive a default value.
 *
 * @example const searchQuery = +FragmentId:(
 * c8f9b9bc0dd147e4947c12d6c0387510179daf4d5c0c4894ac0ab7a58df2bf159dedcee4e6d64ef898a1cef795d27e1a
 * b36714c2cdd941fd9352561b501b9ae5d7e42b6b4e8245d29c08f75262c1e763422e40181d50478aae59ab89fdf6fe5f
 * ) +((+RecordType:(Record Mh2Collection))'
 */

export class BulkSearchRequest implements SearchRequest {
    Query: string;
    SortField?: SortField;
    SortDirection?: SortDirection;

    constructor(fragments: readonly PartialRecord[], restriction: Restriction) {
        this.Query = this.buildSearchRequestQuery(fragments, restriction);
    }

    private buildSearchRequestQuery(fragments: readonly PartialRecord[], restriction: Restriction): string {
        const fragmentIds: FragmentId[] = fragments.map((fragment: PartialRecord) => fragment.getFragmentId());

        const structuredQuery: StructuredQuery = GenerateStructuredQueryFromAdvancedSearchTerm(
            'FragmentId',
            fragmentIds,
            Operand.Should
        );
        return ApiQueryBuilder.queryToLuceneQuery(new ApiQuery(structuredQuery, restriction));
    }
}

export class BatchSearchRequest implements SearchRequest {
    Query: string;
    SortField?: SortField;
    SortDirection?: SortDirection;

    constructor(query: ApiQuery) {
        this.Query = ApiQueryBuilder.queryToLuceneQuery(query);
    }
}

export enum ShBatchTaskTypes {
    MERGE = 'BatchMergeTask',
    MERGE_MULTIPLE = 'BatchMergesTask',
    DIFF = 'BatchApplyDiffTask',
    ADOPT = 'BatchAdoptTask',
    DELETE = 'BatchDeleteTask',
    PUBLISH = 'BatchPublishTask',
    RESTART_FAILED_JOBS = 'RestartFailedJobsTask',
    TOUCH = 'BatchTouchTask',
    RETRANSCODE = 'BatchRetranscodeTask',
    MIGRATE_DATE_FIELDS = 'MigrateDateFieldsTask',
    CSV_TASK = 'BatchCSVTask'
}

export type BatchTaskType = ShBatchTaskTypes;

/**
 * Map over each enum value in {@link ShBatchTaskTypes} and link it to the corresponding task.
 * When creating a {@link BatchOptions} object, you have to specify the type ahead of time, or dynamically using a parameter.
 * This seems like more work, but this then narrows the TaskType parameter
 * and TypeScript will type check the Task property you wish to create.
 * TypeScript will also tell you about which properties you are missing.
 *
 * @example const batchOptions: BatchOptions<BatchTypes.MERGE> = {
 *             TaskType: taskType,
 *             Task: {
 *               Sidecar: [changes.metadata], // error
 *             // Type 'NestedPartial<Metadata>[]' has no properties in common with type 'NestedPartial<Metadata>'.
 *               Reason: 'Bulk save after bulk describe'
 *             },
 *             //Property 'SearchRequest' is missing in type ...
 * }
 */
export type OptionsMap = {
    [ShBatchTaskTypes.MERGE]: MergeTask;
    [ShBatchTaskTypes.MERGE_MULTIPLE]: MultipleMergeTask;
    [ShBatchTaskTypes.DIFF]: DiffTask;
    [ShBatchTaskTypes.ADOPT]: AdoptTask;
    [ShBatchTaskTypes.DELETE]: DeleteTask;
    [ShBatchTaskTypes.PUBLISH]: PublishTask;
    [ShBatchTaskTypes.RESTART_FAILED_JOBS]: RestartJobsTask;
    [ShBatchTaskTypes.TOUCH]: TouchTask;
    [ShBatchTaskTypes.RETRANSCODE]: RetranscodeTask;
    [ShBatchTaskTypes.MIGRATE_DATE_FIELDS]: MigrateDateTask;
    [ShBatchTaskTypes.CSV_TASK]: BatchCsvTask;
};

export interface Tasked<T extends BatchTask> {
    Task: T;
}

export type BatchOptionsMapped<T extends BatchTaskType> = (taskOptions: Partial<OptionsMap[T]>) => OptionsMap[T];
